import {defineStore} from 'pinia';
import {getPromo, removePromo} from "@/modules/chat/services/promo";
import {dispatchCanEditDialog, renameTitle} from "@/modules/chat/services/event";
import {getSocketHash, getModeFromUrl, insertUrlParam} from "@/services/helper";
import {
    DEFAULT_CHAT_MODE,
    DEFAULT_CHAT_MODEL,
    DEFAULT_IMAGE_MODEL, DEFAULT_LK_IMAGE_MODEL, DEFAULT_MUSIC_MODEL,
    DEFAULT_ONLINE_MODEL,
    GENERATE_IMAGE_CHAT_MODE, GENERATE_IMAGE_MODES, IMAGE_VARIATION,
    MESSAGE_TYPE_DRAFT,
    MODE_IMAGE, MODE_MUSIC,
    MODE_ONLINE,
    MODE_TEXT,
    MODE_UML, MUSIC_CHAT_MODE,
    ONLINE_CHAT_MODE,
    UML_CHAT_MODE
} from "@/constants";
import {getSystemPrompt} from "@/modules/chat/services/preset";
import {formDraftMessage} from "@/modules/chat/services/message";
import {socketCallbackOnMessage} from "@/modules/chat/services/socket";
import {useChatStore} from "./chatStore";
import {useInputStore} from "./userInputStore";
import {useReferenceStore} from "./referenceStore";

export const useAppStateStore = defineStore('app', {
    state: () => ({
        blockSending: false,
        initial: true,
        isDarkTheme: false,
        socket: false,
        showLoading: false,
        hideExportButton: true,
        disableAll: false,
        mode: MODE_TEXT,
        showSocial: true,
        showTemplates: true,
        showModels: true,
        showExamples: false,
        showExtendInfo: false,
        canDeleteMessage: false,
        inputLimit: null,
        attachFiles: false,
        limitFiles: 0,
        showSearchDomainFilter: false,
        showMusicFields: false,
        authorized: false,
        features: {
            editMessage: false,
            attachVectorDatabase: false,
            manageAssistants: false,
            imageVariation: false,
        }
    }),
    actions: {
        async initializeApp() {
            this.showSocial = window.chatWidgetConfig?.showSocial ?? true;
            this.showTemplates = window.chatWidgetConfig?.showTemplates ?? true;
            this.showExamples = window.chatWidgetConfig?.showExamples ?? false;
            this.showExtendInfo = window.chatWidgetConfig?.showExtendInfo ?? false;
            this.canDeleteMessage = window.chatWidgetConfig?.canDeleteMessage ?? false;
            this.inputLimit = window.chatWidgetConfig?.inputLimit ?? null;
            this.features.editMessage = window.chatWidgetConfig?.editMessage ?? false;

            this.loadTheme();
            this.applyTheme(this.isDarkTheme);

            this.showLoading = true;

            const promises = [
                useReferenceStore().fetchPresets(),
                useReferenceStore().fetchModels(),
                useReferenceStore().initSettings(),
                ...(this.showExamples ? [useReferenceStore().initExamples()] : [])
            ];
            const [, models] = await Promise.all(promises);
            useReferenceStore().models = models;
            const {presets} = useReferenceStore();

            const chatStore = useChatStore();
            const referenceStore = useReferenceStore();

            const dialogHash = getSocketHash();
            let selectedPreset = null;
            let selectedModel = null;
            const newPreset = window.chatWidgetConfig?.preset;
            if (dialogHash) {
                await chatStore.fetchDialog(dialogHash);
                const {dialog} = chatStore;
                // uml нет в presets (указывается через настройки)
                selectedPreset = presets.find(preset => preset.key === dialog.mode) || newPreset;
                selectedModel = models.find(model => model.id === dialog.model);
            } else {
                const modelFromInitialConfig = window.chatWidgetConfig?.model;
                if (modelFromInitialConfig) {
                    const modelForUpdate = referenceStore.map_url_to_model[modelFromInitialConfig];
                    if (modelForUpdate) {
                        selectedModel = models.find(model => model.id === modelForUpdate);
                    }
                    let mode = getModeFromUrl();
                    if (selectedModel && selectedModel.image) {
                        mode = GENERATE_IMAGE_CHAT_MODE;
                    }
                    selectedPreset = presets.find(preset => preset.key === mode);
                } else if (newPreset) {
                    selectedPreset = newPreset;
                } else {
                    const mode = getModeFromUrl();
                    selectedPreset = presets.find(preset => preset.key === mode);
                }
            }

            useInputStore().selectPreset(selectedPreset);
            this.setModeByPreset(selectedPreset);

            if (typeof(selectedModel) === "undefined" || selectedModel === null ) {
                selectedModel = this.getDefaultModel();
            }
            useInputStore().selectModel(selectedModel);
            this.setShowSearchDomainFilter(selectedModel.online);
            this.setShowMusicFields(selectedModel.music);
            const canImageVariation = selectedModel.image_features.includes(IMAGE_VARIATION);
            this.setImageVariationFeature(canImageVariation);

            if (this.mode === MODE_UML) {
                await useReferenceStore().initUmlModes();
                const umlModeKey = window.chatWidgetConfig?.uml_mode ?? useReferenceStore().uml_modes[2].key;
                const umlMode = useReferenceStore().uml_modes.find(umlModeItem => umlModeItem.key === umlModeKey);
                useInputStore().selectUmlMode(umlMode);
                this.disableModels();
                this.setHideExportButton(true);
            }
            if (this.mode === MODE_IMAGE) {
                this.setAttachFiles(canImageVariation);
                this.setLimitFiles(1);
            } else {
                this.setAttachFiles(selectedPreset.can_attach_files);
                this.setLimitFiles(0);
            }

            this.initial = false;
            if (dialogHash) {
                await useChatStore().fetchMessages(dialogHash);
                dispatchCanEditDialog(dialogHash);
                await this.initSocket(socketCallbackOnMessage, dialogHash);
            }

            if (!dialogHash) {
                const promo = getPromo();
                if (promo && promo.trim()) {
                    useInputStore().inputValue = promo;
                    const result = await this.handleSending();
                    if (result !== false) {
                        useInputStore().resetInputValue();
                    }
                    removePromo();
                }
            }
            this.showLoading = false;
        },
        setAttachFiles(value) {
            this.attachFiles = value;
        },
        setLimitFiles(value) {
            this.limitFiles = value;
        },
        setModeByPreset(selectedPreset) {
            let mode = MODE_TEXT;
            if (GENERATE_IMAGE_MODES.includes(selectedPreset.key)) {
                mode = MODE_IMAGE;
            } else if (selectedPreset.key === ONLINE_CHAT_MODE) {
                mode = MODE_ONLINE;
            } else if (selectedPreset.key === UML_CHAT_MODE) {
                mode = MODE_UML;
            } else if (selectedPreset.key === MUSIC_CHAT_MODE) {
                mode = MODE_MUSIC;
            }
            this.setMode(mode);
            if (GENERATE_IMAGE_MODES.includes(selectedPreset.key)) {
                this.setHideExportButton(true);
            } else if (selectedPreset.key === UML_CHAT_MODE) {
                this.setHideExportButton(true);
            } else if (selectedPreset.key === MUSIC_CHAT_MODE) {
                this.setHideExportButton(true);
            } else {
                this.setHideExportButton(false);
            }
        },
        getDefaultModel() {
            let defaultModel = useReferenceStore().default_text_model;
            if (this.mode === MODE_IMAGE) {
                defaultModel = this.authorized ? DEFAULT_LK_IMAGE_MODEL: DEFAULT_IMAGE_MODEL;
            } else if (this.mode === MODE_ONLINE) {
                defaultModel = DEFAULT_ONLINE_MODEL;
            } else if (this.mode === MODE_MUSIC) {
                defaultModel = DEFAULT_MUSIC_MODEL;
            }
            return useReferenceStore().models.find(model => model.id === defaultModel);
        },
        socketOn() {
            this.socket = true;
        },
        setShowSearchDomainFilter(status) {
            this.showSearchDomainFilter = status;
        },
        setShowMusicFields(status) {
            this.showMusicFields = status;
        },
        setAttachVectorDatabase(status) {
            this.features.attachVectorDatabase = status;
        },
        setImageVariationFeature(status) {
            this.features.imageVariation = status;
        },
        disableModels() {
            this.showModels = false;
        },
        async initSocket(socketCallback, dialogHash) {
            const { initSocket } = await import('@/services/socket');
            await initSocket(socketCallback, dialogHash);
            this.socketOn();
        },
        setMode(mode) {
            this.mode = mode;
        },
        setDisableAll(disable) {
            this.disableAll = disable;
        },
        setHideExportButton(hide) {
            this.hideExportButton = hide;
        },
        startBlockSending() {
            this.blockSending = true;
        },
        stopBlockSending() {
            this.blockSending = false;
        },
        async handleSending(files = null) {
            const inputStore = useInputStore();
            const message = inputStore.inputValue;

            this.startBlockSending();

            const chatStore = useChatStore();
            if (chatStore.messages.length === 0) {
                this.showLoading = true;
            }

            const { assistants} = chatStore;
            let {dialog} = chatStore;
            if (!dialog) {
                const {selectedPreset, selectedModel} = inputStore;
                const mode = selectedPreset?.key ?? DEFAULT_CHAT_MODE;
                const model = selectedModel.id ?? DEFAULT_CHAT_MODEL;
                const preset = getSystemPrompt();
                let assistantsIds = null;
                if (assistants && assistants.length) {
                    assistantsIds = assistants.map(assistant => assistant.assistant_id);
                }
                let webSearch = null;
                if (this.canWebSearch()) {
                    webSearch = inputStore.webSearch;
                }
                dialog = await chatStore.createDialog(message, mode, model, preset, assistantsIds, webSearch);

                if (!dialog) {
                    this.stopBlockSending();
                    this.showLoading = false;
                    return false;
                }
                insertUrlParam('c', dialog.id);

                renameTitle(dialog.title);
                dispatchCanEditDialog(dialog.id);

                if (!this.socket) {
                    await this.initSocket(socketCallbackOnMessage, dialog.id);
                }
            }

            const {selectedUmlMode, searchDomainFilter, musicTags, musicTitle} = inputStore;
            const uml = selectedUmlMode?.key ?? null;
            const result = await chatStore.sendMessage(message, dialog.id, files, uml, searchDomainFilter, musicTitle, musicTags);

            this.showLoading = false;

            return result;
        },
        async handleRegenerateErrorSending(innerMessageId, realMessageId) {
            this.startBlockSending();
            const chatStore = useChatStore();
            chatStore.replaceMessageType(innerMessageId, {type: MESSAGE_TYPE_DRAFT});

            chatStore.setError(null);
            return await chatStore.regenerateMessage(realMessageId);
        },
        async handleRetryJobId(innerMessageId, jobId) {
            this.startBlockSending();
            const chatStore = useChatStore();
            chatStore.replaceMessageType(innerMessageId, {type: MESSAGE_TYPE_DRAFT});

            chatStore.setError(null);
            return await chatStore.retryJob(jobId);
        },
        async handleRetryBatchJobId(batchJobId) {
            this.startBlockSending();
            const chatStore = useChatStore();
            chatStore.removeErrorMessage();

            // все ассистенты error, cancel в waiting
            chatStore.setStatusBatchProgressAssistantWaiting(batchJobId);

            chatStore.setError(null);
            return await chatStore.retryBatchJob(batchJobId);
        },
        async handleRegenerateMessageSending(assistantMessageId) {
            this.startBlockSending();
            const chatStore = useChatStore();
            const {mode} = this;

            const draftMessage = formDraftMessage({id: parseInt(assistantMessageId, 10)});
            if ([MODE_TEXT, MODE_ONLINE].includes(mode)) {
                chatStore.replaceMessage(assistantMessageId, draftMessage);
            } else {
                chatStore.addDraftMessage(draftMessage);
            }
            chatStore.setError(null);
            return await chatStore.regenerateMessage(assistantMessageId);
        },
        canWebSearch() {
            const inputStore = useInputStore();
            return this.authorized && inputStore?.selectedModel.web_search && inputStore.selectedPreset.web_search
        },
        loadTheme() {
            const savedTheme = localStorage.getItem('theme');
            const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            this.isDarkTheme = savedTheme ? savedTheme === 'dark' : prefersDark;
        },
        applyTheme(isDark) {
            if (isDark) {
                document.documentElement.classList.add('my-app-dark');
                document.documentElement.setAttribute('data-theme', 'dark');
                localStorage.setItem('theme', 'dark');
            } else {
                document.documentElement.classList.remove('my-app-dark');
                document.documentElement.setAttribute('data-theme', 'light');
                localStorage.setItem('theme', 'light');
            }
        },
        toggleTheme() {
            this.applyTheme(this.isDarkTheme);
        }
    }
});